<template>
    <li
        class="MenuElement"
        :class="[ {'MenuElement--active': active}, {'MenuElement--disabled': disabled} , additionalClass ]"
    >
        <a
            :href="url"
            :target="target"
            class="MenuElement__link"
            @click="emitClick"
            @mouseenter="$emit('mouseenter')"
            @mouseleave="$emit('mouseleave')"
        >
            <span v-if="currentIcon" class="MenuElement__linkIcon">
                <EidIcon :icon-src="currentIcon" :icon-props="iconProps" :default-icon-svg="defaultIconSvg" />
            </span>
            <slot name="label">
                <span class="MenuElement__linkText">
                    {{ label }}
                </span>
            </slot>
            <div class="MenuElement__endSectionContainer">
                <div
                    v-if="showNotificationBadge"
                    class="MenuElement__notificationBadge"
                    :class="{'MenuElement__notificationBadge--max': notificationCount > 99}"
                >
                    {{ notificationCount }}
                </div>
                <div v-if="hasSubNavigation" class="MenuElement__linkChevron">
                    <FontAwesomeIcon :icon="caret" />
                </div>
            </div>
        </a>
        <Menu v-if="open && hasSubNavigation">
            <slot />
        </Menu>
    </li>
</template>

<style lang="scss">
$eidDangerRed: #f15b47;

.MenuElement {
    &__endSectionContainer {
        margin-left: auto;
        display: flex;
        align-items: center;
        justify-content: flex-end;
    }

    &__notificationBadge {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 25px;
        height: 25px;
        border-radius: 100%;
        font-size: 14px;
        background-color: $eidDangerRed;
        color: #fff;

        &--max {
            font-size: 12px;
        }
    }
}
</style>

<script lang="ts">
    import { Component, Prop, Vue } from 'vue-property-decorator';

    import { IconDefinition, faAngleDown, faAngleUp } from '@fortawesome/pro-light-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import EidIcon from '../EidIcon/EidIcon.vue';

    import Menu from './Menu.vue';

    interface NotificationConfig {
        fetchCount: (() => Promise<void>) | null;
        getCount: (() => number) | null;
    }

    @Component({
        emits: [ 'click', 'mouseenter', 'mouseleave' ],
        components: { Menu, FontAwesomeIcon, EidIcon },
        // PRODUCT-26110 Task 10 (INSTANCE_LISTENERS): this component's root (`<li>`) is
        // NOT the element that raises click/mouseenter/mouseleave -- the nested `<a>`
        // catches those natively and re-emits them explicitly via `emitClick`/inline
        // `$emit`. Vue 3 merges listeners into `$attrs`, so without `inheritAttrs: false`
        // a caller's `@click` would now ALSO auto-fall-through onto the `<li>` root as a
        // native listener -- since a click on the nested `<a>` bubbles up to `<li>`,
        // that fires the caller's handler a second time (once via the explicit `$emit`,
        // once via the native bubble), on top of the already-explicit re-emit. No call
        // site relies on other attrs reaching `<li>` (verified: every usage passes only
        // declared props + `@click`).
        inheritAttrs: false,
    })
    export default class MenuLink extends Vue {
        @Prop({ type: Boolean, default: false })
        private active!: boolean;

        @Prop({ type: String, default: '' })
        private additionalClass!: string;

        @Prop({ type: Boolean, default: false })
        private disabled!: boolean;

        @Prop({ type: Boolean, default: false })
        private open!: boolean;

        @Prop({ type: String, default: null })
        private href!: string | null;

        @Prop({ type: String, default: null })
        private target!: string | null;

        @Prop({ default: null })
        private icon!: IconDefinition | string; // FontAwesome icon or image URL

        // this was added due to supporting icon being fetched on url, in case of failure we show a default icon
        @Prop({ default: null })
        private defaultIconSvg!: IconDefinition | string | null;

        @Prop({ default: null })
        private iconActive!: IconDefinition | string; // FontAwesome icon or image URL

        @Prop({ type: Object, default: () => ({}) })
        private iconProps!: object;

        @Prop({ type: String })
        private label!: string;

        @Prop({ type: Object, default: null })
        private notificationConfig!: NotificationConfig | null;

        private async mounted() {
            if (this.notificationConfig?.fetchCount) {
                await this.notificationConfig.fetchCount();
            }
        }

        private get showNotificationBadge(): boolean {
            if (!this.notificationConfig) {
                return false;
            }
            return this.notificationCount > 0;
        }

        private get notificationCount(): number {
            return this.notificationConfig?.getCount?.() || 0;
        }

        private emitClick(event: MouseEvent) {
            if (!this.disabled) {
                this.$emit('click', event);
            }
        }

        private get hasSubNavigation() {
            return Boolean(this.$slots.default);
        }

        private get currentIcon() {
            return this.active ? this.iconActive || this.icon : this.icon;
        }

        private get url() {
            return this.href || 'javascript:void(0)';
        }

        private get caret() {
            return this.open ? faAngleUp : faAngleDown;
        }
    }
</script>
